<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary">
          mdi-card-text-outline
        </v-icon>
        <span class="px-2">{{ $t('Subscription Plans') }}</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="showPlanDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('Add Plan') }}
        </v-btn>
      </v-card-title>
      <v-data-table
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('loading_text')"
        :headers="translatedHeaders"
        :items="plans"
        :search="search"
        :footer-props="footerProps"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.price="{ item }">
          {{ item.price }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip :color="item.status === 'active' ? 'success' : 'grey'" dark>
            {{ $t(item.status) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon
            small
            class="mx-2"
            @click="editPlan(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            class="mx-2"
            @click="toggleStatus(item)"
          >
            {{ item.status === 'active' ? 'mdi-eye-off' : 'mdi-eye' }}
          </v-icon>
          <v-icon
            small
            class="mx-2"
            @click="deletePlan(item, plans.indexOf(item))"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-row justify="center">
      <v-dialog
        v-model="planDialog"
        persistent
        max-width="600px"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
        >
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ id ? $t('Edit Plan') : $t('Add Plan') }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="name"
                      :rules="nameRules"
                      :label="$t('Plan Name') + '*'"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="duration_days"
                      :rules="durationRules"
                      :label="$t('Duration (Days)') + '*'"
                      type="number"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="price"
                      :rules="priceRules"
                      :label="$t('Price') + '*'"
                      type="number"
                      step="0.01"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="status"
                      :items="statusOptions"
                      :rules="statusRules"
                      :label="$t('Status') + '*'"
                      required
                    ></v-select>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="planDialog = false"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                @click="savePlan"
              >
                {{ $t('Save Changes') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>
export default {
  data() {
    return {
      plans: [],
      isLoading: false,
      search: "",
      planDialog: false,
      valid: true,
      id: null,
      name: '',
      duration_days: '',
      price: '',
      status: 'active',
      statusOptions: [
        { text: this.$t('active'), value: 'active' },
        { text: this.$t('inactive'), value: 'inactive' }
      ],
      nameRules: [
        v => !!v || this.$t('plan_name_required'),
      ],
      durationRules: [
        v => !!v || this.$t('duration_required'),
        v => /^[0-9]+$/.test(v) || this.$t('duration_must_be_integer'),
        v => parseInt(v) > 0 || this.$t('duration_must_be_greater_than_0'),
      ],
      priceRules: [
        v => !!v || this.$t('price_required'),
        v => !isNaN(parseFloat(v)) || this.$t('price_must_be_number'),
        v => parseFloat(v) >= 0 || this.$t('price_must_be_positive'),
      ],
      statusRules: [
        v => !!v || this.$t('status_required'),
      ],
      headers: [
        { text: "id", value: "id", align: "start", filterable: false },
        { text: "Plan Name", value: "name" },
        { text: "Duration (Days)", value: "duration_days" },
        { text: "Price", value: "price" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text)
      }));
    },
    footerProps() {
      return {
        itemsPerPageText: this.$t("rows_per_page"),
      }
    }
  },
  mounted() {
    this.loadPlans();
  },
  methods: {
    loadPlans() {
      this.isLoading = true;
      axios
        .get(`/subscription-plans/all`)
        .then((response) => {
          this.plans = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("error_retrieving_plans"),
            type: 'error'
          });
          console.log(error);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    showPlanDialog() {
      this.id = null;
      this.name = '';
      this.duration_days = '';
      this.price = '';
      this.status = 'active';
      this.planDialog = true;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    editPlan(plan) {
      this.id = plan.id;
      this.name = plan.name;
      this.duration_days = plan.duration_days;
      this.price = plan.price;
      this.status = plan.status;
      this.planDialog = true;
    },
    toggleStatus(plan) {
      const newStatus = plan.status === 'active' ? 'inactive' : 'active';
      axios
        .post(`/subscription-plans/toggle-status`, {
          id: plan.id,
          status: newStatus
        })
        .then(() => {
          plan.status = newStatus;
          this.$notify({
            title: this.$t("Success"),
            text: this.$t("plan_status_updated"),
            type: 'success'
          });
        })
        .catch((error) => {
          this.$notify({
            title: this.$t("Error"),
            text: this.$t("plan_status_update_failed"),
            type: 'error'
          });
          console.log(error);
        });
    },
    savePlan() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.planDialog = false;
        axios
          .post(`/subscription-plans/create-edit`, {
            plan: {
              id: this.id,
              name: this.name,
              duration_days: this.duration_days,
              price: this.price,
              status: this.status
            }
          })
          .then(() => {
            this.loadPlans();
            this.$notify({
              title: this.$t("Success"),
              text: this.id ? this.$t("plan_updated") : this.$t("plan_created"),
              type: 'success'
            });
          })
          .catch((error) => {
            this.$notify({
              title: this.$t("Error"),
              text: this.$t("plan_save_failed"),
              type: 'error'
            });
            console.log(error);
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    },
    deletePlan(plan, index) {
      this.$swal
        .fire({
          title: this.$t("plan_delete_title"),
          text: this.$t("plan_delete_text", { name: plan.name }),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("yes_delete"),
          cancelButtonText: this.$t("cancel"),
        })
        .then((result) => {
          if (result.isConfirmed) {
            axios
              .delete(`/subscription-plans/${plan.id}`)
              .then((response) => {
                this.loadPlans();
                this.$notify({
                  title: this.$t("Success"),
                  text: response.data.success ? response.data.success[0] : this.$t("plan_deleted"),
                  type: "success",
                });
              })
              .catch((error) => {
                this.$notify({
                  title: this.$t("Error"),
                  text: this.$t("plan_delete_failed"),
                  type: 'error'
                });
                console.log(error);
              });
          }
        });
    }
  }
};
</script>
